// Per-DEVICE floor settings (spec 06 §3.5 rungs 1 and 2).
//
// Two of the three rungs of the scan-attribution ladder are device facts, and
// neither of them is a user fact:
//
//   rung 1 — "the location QR this DEVICE scanned in the last 120 s". The
//            server derives it from `scan_events` matched on `device_id`, so
//            the device must present a STABLE id across reloads, sessions and
//            offline replays. A per-session id would break the ladder exactly
//            when the outbox replays a bin scan and a bobbin scan together
//            after a dead zone — the pair would look like two different
//            devices and the bobbin would not be attributed.
//   rung 2 — "the station this device is bolted to". §3.5 says the binding
//            "travels on the wire" as `context_location_id`, because device
//            settings are client state the server does not hold.
//
// Both live in localStorage, minted once. The id is a uuid v7 like every other
// identifier the floor mints, and carries no user or tenant information — it is
// a device label, not a credential; authorisation is entirely the session
// cookie's job.

import { uuidV7 } from "../../../lib/uuid";

const DEVICE_ID_KEY = "assessly.floor.deviceId";
const STATION_KEY = "assessly.floor.stationLocationId";

function safeStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/** The stable id this device presents on every scan. Minted on first use. */
export function deviceId(): string {
  const storage = safeStorage();
  if (storage === undefined) return uuidV7();
  try {
    const existing = storage.getItem(DEVICE_ID_KEY);
    if (existing !== null && existing.length > 0) return existing;
    const minted = uuidV7();
    storage.setItem(DEVICE_ID_KEY, minted);
    return minted;
  } catch {
    // No storage: a per-session id is worse than a stable one but better than
    // no scan at all. The consequence — rung 1 cannot match across a reload —
    // is stated on the scan screen when the id could not be persisted.
    return uuidV7();
  }
}

/**
 * True when the device id survives a reload (rung 1 works across restarts).
 *
 * This MINTS BEFORE IT CHECKS, and that is the whole correctness of it. The id
 * is minted lazily by `deviceId()`, which the scan screen calls only when a tap
 * is submitted — so on a healthy device the key does not exist until the first
 * scan. Reading the key alone therefore answered "has this device scanned yet?"
 * while the screen presented it as "can this device save its id?", and every
 * operator opening Floor → Scanning for the first time was told, falsely, that
 * their scans might not link after a restart. A warning that fires on a device
 * with nothing wrong with it is worse than none: it teaches the floor to ignore
 * the strip where the real sync state lives.
 *
 * Minting first makes it a genuine round trip — write, then read back — so it
 * is false only when storage really refuses (private mode, disabled storage, a
 * full quota), which is exactly the condition the message describes.
 */
export function deviceIdIsStable(): boolean {
  const storage = safeStorage();
  if (storage === undefined) return false;
  try {
    deviceId();
    return (storage.getItem(DEVICE_ID_KEY) ?? "").length > 0;
  } catch {
    return false;
  }
}

/** The station location this device is bound to (§3.5 rung 2), if any. */
export function stationLocationId(): string | undefined {
  const storage = safeStorage();
  if (storage === undefined) return undefined;
  try {
    const value = storage.getItem(STATION_KEY);
    return value !== null && value.length > 0 ? value : undefined;
  } catch {
    return undefined;
  }
}

export function setStationLocationId(id: string | undefined): void {
  const storage = safeStorage();
  if (storage === undefined) return;
  try {
    if (id === undefined || id.length === 0) storage.removeItem(STATION_KEY);
    else storage.setItem(STATION_KEY, id);
  } catch {
    // non-fatal — the binding simply does not persist
  }
}
